import React from 'react';
import { ArrowRight } from 'lucide-react';
import { playClickSound } from '@/utils/soundUtils';

interface OrderSummaryFooterProps {
  totalAmount: number;
  onClearOrder: () => void;
  onProceedToFunding: () => void;
  onShowOrderSummary?: () => void;
  className?: string;
}

/**
 * Order Summary Footer component
 * Based on Figma design with Clear Order, Order Summary, and Proceed buttons
 * Updated to match new design specifications
 */
const OrderSummaryFooter: React.FC<OrderSummaryFooterProps> = ({
  totalAmount,
  onClearOrder,
  onProceedToFunding,
  onShowOrderSummary,
  className = '',
}) => {
  const hasOrder = totalAmount > 0;

  return (
    <footer className={`w-full left-0 right-0 z-10 ${className}`}>
      <div className="bg-[#050B18]/90 backdrop-blur-[20px] border-t border-white/20 shadow-[0px_-10px_40px_0px_rgba(0,0,0,0.5)]">
        <div className="h-[80px] max-w-[1280px] mx-auto px-3 sm:px-4 md:px-6 ipad:px-6 lg:px-16 xl:px-20 py-2 sm:py-2 md:py-2.5 flex flex-row items-center justify-between gap-1.5 sm:gap-2 md:gap-3 ipad:gap-4 lg:gap-5">
          {/* Left: Clear Order Button + Order Summary */}
          <div className="flex flex-row items-center gap-1.5 sm:gap-2 md:gap-3 ipad:gap-4 lg:gap-5 xl:gap-6 flex-1 min-w-0">
            {/* Clear Order Button */}
            <button
              onClick={() => {
                if (hasOrder) {
                  playClickSound();
                  onClearOrder();
                }
              }}
              disabled={!hasOrder}
              aria-label="Clear order"
              className="flex flex-shrink-0 items-center justify-center gap-1 sm:gap-1.5 transition-all duration-200 disabled:cursor-not-allowed whitespace-nowrap h-9 sm:h-10 px-2.5 sm:px-3 py-1.5 rounded-lg"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'var(--Linear, linear-gradient(180deg, #FBBF24 0%, #D97706 100%))',
                color: '#1e293b',
                boxShadow: hasOrder
                  ? '0 0 15px 0 rgba(255, 255, 255, 0.60) inset, 0 0 20px 0 rgba(255, 204, 0, 0.40)'
                  : 'none',
                opacity: hasOrder ? 1 : 0.5,
                cursor: hasOrder ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
              }}
              onMouseDown={(e) => {
                if (hasOrder) {
                  e.currentTarget.style.transform = 'scale(0.95)';
                }
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <span
                className="flex-shrink-0 text-[10px] sm:text-xs"
                style={{
                  color: 'var(--Text---black, #000)',
                  textAlign: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontStyle: 'normal',
                  fontWeight: 900,
                  lineHeight: '1.2',
                }}
              >
                Clear Order
              </span>
              <span
                className="flex flex-shrink-0 items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4"
                aria-hidden
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 26" fill="none" className="w-full h-full">
                  <path d="M8.9991 19.9111C7.40614 19.9111 6.05688 19.2222 4.95131 17.8445C3.84575 16.4667 3.29297 14.7852 3.29297 12.8C3.29297 10.8148 3.84575 9.13335 4.95131 7.75558C6.05688 6.3778 7.40614 5.68891 8.9991 5.68891C9.81936 5.68891 10.604 5.90002 11.3529 6.32224C12.1018 6.74447 12.7438 7.34817 13.2787 8.13335V5.68891H14.7052V11.9111H9.71237V10.1334H12.7081C12.3277 9.30373 11.8076 8.65187 11.1478 8.1778C10.488 7.70373 9.77181 7.46669 8.9991 7.46669C7.81032 7.46669 6.79986 7.98521 5.96772 9.02224C5.13557 10.0593 4.7195 11.3185 4.7195 12.8C4.7195 14.2815 5.13557 15.5408 5.96772 16.5778C6.79986 17.6148 7.81032 18.1334 8.9991 18.1334C9.91446 18.1334 10.7407 17.8074 11.4777 17.1556C12.2147 16.5037 12.7319 15.6445 13.0291 14.5778H14.5269C14.1941 16.1482 13.5165 17.4297 12.4941 18.4222C11.4718 19.4148 10.3068 19.9111 8.9991 19.9111Z" fill="currentColor" />
                </svg>
              </span>
            </button>

            {/* Order Summary - compact on mobile for one row */}
            <div
              className="flex flex-col items-start cursor-pointer hover:opacity-80 transition-opacity gap-0 sm:gap-0"
              onClick={() => {
                if (onShowOrderSummary) {
                  playClickSound();
                  onShowOrderSummary();
                }
              }}
            >
              <div className="mb-0 pb-0">
                <span className="text-[#22D3EE] text-[9px] sm:text-[10px] md:text-xs font-black underline truncate block">
                  Order Summary
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  width: 'auto',
                  minWidth: '36px',
                  height: 'auto',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  color: '#FFF',
                }}
                className="sm:h-8"
              >
                <span
                  style={{
                    fontFamily: '"JetBrains Mono"',
                    fontSize: 'clamp(10px, 2.2vw, 24px)',
                    fontStyle: 'normal',
                    fontWeight: 800,
                    lineHeight: 'clamp(14px, 2.8vw, 28px)',
                    letterSpacing: '-0.8px',
                    color: '#FFF',
                  }}
                  className="truncate"
                >
                  ${Math.round(totalAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Proceed to Funding Button - same size as Clear Order */}
          <button
            onClick={() => {
              if (hasOrder) {
                playClickSound();
                onProceedToFunding();
              }
            }}
            disabled={!hasOrder}
            className="flex justify-center items-center gap-1 sm:gap-1.5 transition-all duration-200 rounded-lg px-2.5 sm:px-3 py-1.5 text-white font-black text-[10px] sm:text-xs uppercase tracking-wider whitespace-nowrap flex-shrink-0 h-9 sm:h-10 min-w-[72px] sm:min-w-0 disabled:cursor-not-allowed"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'linear-gradient(180deg, #D946EF 0%, #7B2CBF 100%)',
              boxShadow: '0 0 15px 0 rgba(255, 255, 255, 0.40) inset, 0 0 20px 0 rgba(123, 44, 191, 0.50)',
              opacity: hasOrder ? 1 : 0.5,
              cursor: hasOrder ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}
            onMouseDown={(e) => {
              if (hasOrder) {
                e.currentTarget.style.transform = 'scale(0.95)';
              }
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <span className="truncate hidden xs:inline">PROCEED TO FUNDING</span>
            <span className="truncate xs:hidden">FUNDING</span>
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" aria-hidden />
          </button>
        </div>
        <div className="h-[env(safe-area-inset-bottom,0px)] bg-[#050B18]" />
      </div>
    </footer>
  );
};

export default OrderSummaryFooter;
